import { GraphConfig } from "@/modules/MapView/Services/Types/UserCustomisableGraphTypes";
import { FormatDateTime, FormatTime } from "@/core/Services/GlobalFunctions";
import { ApexOptions } from "apexcharts";

export default class UserCustomisableGraphService {
  generateGraphSeriesOptions(
    {
      yPropertyConfig,
      xPropertyName,
      xaxisType = "category",
      graphType,
      seriesNames,
      graphTitle,
      colors,
      types,
      dateFormat = "hours",
      dataLabels = false,
    }: GraphConfig,
    data: any[]
  ): { series: ApexAxisChartSeries; options: ApexOptions } {
    const series: ApexAxisChartSeries = [];
    seriesNames.forEach((name, idx) => {
      const generatedSeries = this._generateSeries(
        name,
        yPropertyConfig[idx].id,
        xPropertyName,
        data,
        types && types[idx]
      );

      series.push(generatedSeries);
    });

    const options = this._generateOptions(
      graphTitle,
      graphType,
      colors,
      yPropertyConfig,
      dateFormat,
      xaxisType,
      dataLabels
    );

    return { series, options };
  }

  private _generateSeries(
    seriesName: string,
    yPropertyName: string,
    xPropertyName: string,
    data: any[],
    type?: string
  ) {
    return {
      name: seriesName,
      data: this._mapData(yPropertyName, xPropertyName, data),
      ...(type && { type: type }),
      group: "group"
    };
  }

  private _generateOptions(
    graphTitle: string,
    graphType: any,
    colors: string[],
    yaxisConfig: {
      id: string;
      visible: boolean;
      opposite: boolean;
      title: string;
      min: number;
      max?: number;
      unit?: string;
    }[],
    dateFormat: string,
    xaxisType: any,
    dataLabels: boolean
  ): ApexOptions {
    return {
      chart: {
        id: "apex-chart",
        type: graphType,
        animations: {
          enabled: false,
        },
        zoom: {
          enabled: true,
          type: "x",
        },
        stacked: true,
      },
      stroke: {
        curve: ["straight", "straight", "smooth"],
      },
      title: {
        text: graphTitle,
      },
      dataLabels: {
        enabled: dataLabels,
        formatter: (value: number, { seriesIndex }) => {
          return value.toFixed(0);
        },
      },
      colors: colors,
      xaxis: {
        type: xaxisType,
        labels: {
          formatter: (date: string) => {
            if(dateFormat == "date"){
              return FormatDateTime(date);
            }else if(dateFormat == "none"){
              return date;
            }else {
              return FormatTime(date);
            }
          },
          style: {
            fontSize: "10px",
          },
        },
      },
      yaxis: yaxisConfig.map((yaxis) => {
        return {
          seriesName: yaxis.id,
          show: yaxis.visible,
          opposite: yaxis.opposite,
          min: yaxis.min,
          ...(yaxis.max && { max: yaxis.max }),
          title: {
            text: yaxis.title,
          },
          labels: {
            formatter: (value: number) => {
              return value.toFixed(0);
            },
          },
        };
      }),
      tooltip: {
        y: {
          formatter: (value: number, { seriesIndex }) => {
            return yaxisConfig[seriesIndex].unit ? value.toFixed(0) + " " + yaxisConfig[seriesIndex].unit : value.toFixed(0);
          },
        }
      }
    };
  }

  private _mapData(yPropertyName: string, xPropertyName: string, data: any[]) {
    return data?.map((item) => {
      return { x: item[xPropertyName], y: item[yPropertyName] };
    });
  }
}
